import { useEffect, useRef, useState } from 'react'
import type { EventConfig } from '@/types'
import { ExternalLink, Plus, Trash2 } from 'lucide-react'
import { SCREENSAVER_STYLES, type ScreensaverStyle } from '../../../server/lib/eventSettings'
import { STYLE_USES_VIDEO } from '@/booth/screensaver/Screensaver'
import { useI18n } from '@/lib/i18n'

// Thumbnail colours that hint at each style in the picker
const STYLE_SWATCH: Record<ScreensaverStyle, string> = {
  classic: 'linear-gradient(135deg, #1f2937, #4b5563)',
  shutter: 'repeating-conic-gradient(from 10deg at 50% 50%, #2c2d33 0 20deg, #121215 20deg 40deg)',
  flash: 'radial-gradient(circle at 50% 30%, #ffffff 0%, #9cc3ff 8%, #0b1020 38%, #000 70%)',
  kids: 'linear-gradient(135deg, #5ec8ff, #8b7bff 40%, #ff7ac0 70%, #ffd36b)',
  business: 'linear-gradient(120deg, #070b14 55%, #d4b483 56%, #070b14 58%)',
  party: 'radial-gradient(circle at 30% 30%, #ff2bd6 0%, transparent 35%), radial-gradient(circle at 75% 60%, #22d3ee 0%, transparent 35%), #12031c',
}

function previewUrl(style: ScreensaverStyle, video: string, phrases: string[] | undefined, wake: string, ui: boolean) {
  const q = new URLSearchParams({ style, wake })
  if (video) q.set('video', video)
  if (!ui) q.set('ui', '0')
  return `/screensaver-preview?${q}${phrases ? `#${new URLSearchParams({ p: JSON.stringify(phrases) })}` : ''}`
}

/** A 1920×1080 preview scaled down to fit, so vmin-based layouts look like the kiosk. */
function LivePreview({ src }: { src: string }) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.3)
  // Wait for typing to settle so editing a phrase does not reload the preview per keystroke
  const [shown, setShown] = useState(src)
  useEffect(() => {
    const id = setTimeout(() => setShown(src), 700)
    return () => clearTimeout(id)
  }, [src])
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(el.clientWidth / 1920))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return (
    <div ref={boxRef} className="relative aspect-video w-full overflow-hidden rounded-lg border border-border bg-black">
      <iframe
        key={shown}
        src={shown}
        title="Screensaver preview"
        className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
        style={{ width: 1920, height: 1080, transform: `scale(${scale})` }}
      />
    </div>
  )
}

interface ScreensaverSettingsProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function ScreensaverSettings({ event, onChange }: ScreensaverSettingsProps) {
  const { capture } = event
  const { t } = useI18n()
  const style: ScreensaverStyle = capture.attractLoop?.style || 'classic'
  const preview = (ui: boolean) => previewUrl(style, capture.attractLoop?.videoAsset || '', capture.attractLoop?.phrases, capture.attractLoop?.wakeMethod || 'touch', ui)
  const [newPhrase, setNewPhrase] = useState('')
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])

  useEffect(() => {
    if (capture.attractLoop?.wakeMethod !== 'motion') return
    let cancelled = false
    ;(async () => {
      try {
        // Labels are empty until camera permission has been granted once
        const first = await navigator.mediaDevices.enumerateDevices()
        if (!first.some((d) => d.kind === 'videoinput' && d.label)) {
          await navigator.mediaDevices.getUserMedia({ video: true }).then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {})
        }
        const devs = await navigator.mediaDevices.enumerateDevices()
        if (!cancelled) setCameras(devs.filter((d) => d.kind === 'videoinput'))
      } catch {
        if (!cancelled) setCameras([])
      }
    })()
    return () => { cancelled = true }
  }, [capture.attractLoop?.wakeMethod])

  const updateAttractLoop = (updates: any) => {
    onChange({
      capture: {
        ...capture,
        attractLoop: {
          ...capture.attractLoop,
          ...updates
        }
      }
    })
  }

  const addPhrase = () => {
    if (!newPhrase.trim()) return
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases.push(newPhrase.trim())
    updateAttractLoop({ phrases })
    setNewPhrase('')
  }

  const removePhrase = (index: number) => {
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases.splice(index, 1)
    updateAttractLoop({ phrases })
  }

  const updatePhrase = (index: number, value: string) => {
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases[index] = value
    updateAttractLoop({ phrases })
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold mb-1">Screensaver (Attract Loop)</h2>
        <p className="text-sm text-muted-foreground">Configure the video and overlays that play when the booth is idle.</p>
      </div>

      <div className="bg-card rounded-xl border border-border p-6 space-y-6">
        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
          <input 
            type="checkbox" 
            checked={capture.attractLoop?.enabled || false} 
            onChange={(e) => updateAttractLoop({ enabled: e.target.checked })} 
          />
          <div>
            <div className="text-sm font-medium">Enable Attract Loop</div>
            <div className="text-xs text-muted-foreground">Play a video when no one is using the booth.</div>
          </div>
        </label>

        {capture.attractLoop?.enabled && (
          <>
            <div className="space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <label className="text-sm font-medium">{t('screensaver.title')}</label>
                  <p className="text-xs text-muted-foreground">{t('screensaver.help')}</p>
                </div>
                <a
                  href={preview(true)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  <ExternalLink size={14} /> {t('screensaver.preview')}
                </a>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SCREENSAVER_STYLES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => updateAttractLoop({ style: s })}
                    className={`overflow-hidden rounded-lg border text-left transition-colors ${s === style ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground/40'}`}
                  >
                    <div className="h-14 w-full" style={{ background: STYLE_SWATCH[s] }} />
                    <div className="p-2.5">
                      <div className="text-sm font-medium">{t(`screensaver.styles.${s}.name`)}</div>
                      <div className="text-xs leading-snug text-muted-foreground">{t(`screensaver.styles.${s}.desc`)}</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">{t('screensaver.livePreview')} · {t(STYLE_USES_VIDEO[style] ? 'screensaver.usesVideo' : 'screensaver.noVideo')}</div>
                <LivePreview src={preview(false)} />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Idle Timeout</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="5" 
                  max="300" 
                  value={capture.attractLoop?.timeout || 60} 
                  onChange={(e) => updateAttractLoop({ timeout: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.timeout || 60}s</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Wake Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => updateAttractLoop({ wakeMethod: 'touch' })}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${(capture.attractLoop?.wakeMethod === 'touch' || !capture.attractLoop?.wakeMethod) ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted'}`}
                >
                  Touch Based
                </button>
                <button
                  onClick={() => updateAttractLoop({ wakeMethod: 'motion' })}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${capture.attractLoop?.wakeMethod === 'motion' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted'}`}
                >
                  Motion Based
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Video Path</label>
              <input 
                type="text" 
                value={capture.attractLoop?.videoAsset || '/video/bg_video.mp4'} 
                onChange={(e) => updateAttractLoop({ videoAsset: e.target.value })} 
                className="w-full p-2 border rounded-md bg-background text-foreground text-sm"
                placeholder="/video/bg_video.mp4"
              />
              <p className="text-xs text-muted-foreground">Path to the video file in the public folder.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Max Messages on Screen</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={capture.attractLoop?.maxMessages || 4} 
                  onChange={(e) => updateAttractLoop({ maxMessages: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.maxMessages || 4}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Interval Between Messages</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="0.5" 
                  max="10" 
                  step="0.5"
                  value={capture.attractLoop?.intervalSeconds || 2.5} 
                  onChange={(e) => updateAttractLoop({ intervalSeconds: parseFloat(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.intervalSeconds || 2.5}s</span>
              </div>
            </div>

            {capture.attractLoop?.wakeMethod === 'motion' && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Motion Camera</label>
                <select
                  value={capture.attractLoop?.motionCameraDeviceId || ''}
                  onChange={(e) => {
                    const id = e.target.value
                    const label = cameras.find((d) => d.deviceId === id)?.label || ''
                    updateAttractLoop({ motionCameraDeviceId: id, motionCameraLabel: label })
                  }}
                  className="w-full p-2 border rounded-md bg-background text-foreground text-sm"
                >
                  <option value="">Same as photobooth camera</option>
                  <option value="__other__">Any other camera on the kiosk (e.g. built-in webcam)</option>
                  {cameras.length > 0 && (
                    <optgroup label="Cameras on this computer">
                      {cameras.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 5)}`}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <p className="text-xs text-muted-foreground">Camera used only to detect people for waking the screensaver, so the photobooth camera can switch off while the screensaver runs. The list below shows the cameras of the computer you are using now, which may not be the kiosk. "Any other camera on the kiosk" works on every kiosk with a built-in webcam.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Motion Sensitivity</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="1" 
                  max="100" 
                  value={capture.attractLoop?.motionSensitivity || 50} 
                  onChange={(e) => updateAttractLoop({ motionSensitivity: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.motionSensitivity || 50}%</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Hype Phrases</label>
                <p className="text-xs text-muted-foreground">Phrases that will pop up randomly on top of the video.</p>
              </div>

              <div className="space-y-2">
                {(capture.attractLoop?.phrases || []).map((phrase, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input 
                      type="text" 
                      value={phrase} 
                      onChange={(e) => updatePhrase(index, e.target.value)} 
                      className="flex-1 p-2 border rounded-md bg-background text-foreground text-sm"
                    />
                    <button 
                      onClick={() => removePhrase(index)} 
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                      title="Remove phrase"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  value={newPhrase} 
                  onChange={(e) => setNewPhrase(e.target.value)} 
                  className="flex-1 p-2 border rounded-md bg-background text-foreground text-sm"
                  placeholder="Add a new phrase..."
                  onKeyDown={(e) => e.key === 'Enter' && addPhrase()}
                />
                <button 
                  onClick={addPhrase} 
                  className="p-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
                  title="Add phrase"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
